export const validarCuit = (value) => {
  if (!value) {
    return { ok: false, mensaje: "" };
  }

  const cuit = value.replace(/-/g, "");

  // solo números
  if (!/^\d+$/.test(cuit)) {
    return { ok: false, mensaje: "El CUIT solo puede contener números" };
  }

  // largo exacto
  if (cuit.length !== 11) {
    return { ok: false, mensaje: "El CUIT debe tener 11 dígitos" };
  }

  // evitar 00000000000, 11111111111, etc
  if (/^(\d)\1{10}$/.test(cuit)) {
    return { ok: false, mensaje: "CUIT inválido" };
  }

  // prefijos válidos
  const prefijosValidos = ["20", "23", "24", "27", "30", "33", "34"];
  if (!prefijosValidos.includes(cuit.substring(0, 2))) {
    return { ok: false, mensaje: "Prefijo de CUIT inválido" };
  }

  // dígito verificador AFIP
  let acumulado = 0;
  const digitos = cuit.split("").map(Number);
  const digitoVerificador = digitos.pop();

  for (let i = 0; i < digitos.length; i++) {
    acumulado += digitos[9 - i] * (2 + (i % 6));
  }

  let verif = 11 - (acumulado % 11);
  if (verif === 11) verif = 0;
  if (verif === 10) verif = 9;

  if (digitoVerificador !== verif) {
    return { ok: false, mensaje: "CUIT inválido" };
  }

  return { ok: true, mensaje: "CUIT verificado" };
};

export const validarTelefono = (value) => {
  if (!value) {
    return { ok: false, mensaje: "" };
  }

  // limpiar: solo números
  const tel = value.replace(/\D/g, "");

  // evitar 0000000000, 1111111111, etc
  if (/^(\d)\1+$/.test(tel)) {
    return { ok: false, mensaje: "Teléfono inválido" };
  }

  // largo válido Argentina (sin +54)
  // fijo: 8–10 | celular: 10–11
  if (tel.length < 8 || tel.length > 11) {
    return { ok: false, mensaje: "Teléfono inválido" };
  }

  return { ok: true, mensaje: "Teléfono válido" };
};

/** Régimen directo (convenio 3). */
export const esConvenioDirecto = (idConvenio) => Number(idConvenio) === 3;

/**
 * Al tipear: fuerza patrón 0, + hasta 4 dígitos.
 * Con permiteUno (régimen directo) admite 1 / 1,0000.
 */
export function sanitizarCoeficienteInput(raw, { permiteUno = false } = {}) {
  if (raw == null || raw === "") return "";
  const s = String(raw).replace(/\./g, ",");

  if (permiteUno && /^1/.test(s)) {
    if (s === "1") return "1";
    if (s.startsWith("1,")) {
      const dec = s.slice(2).replace(/[^0]/g, "").slice(0, 4);
      return `1,${dec}`;
    }
    return "1";
  }

  if (s === "0") return "0";
  if (s === "0,") return "0,";

  const match = s.match(/^0,(\d{0,4})/);
  if (match) return `0,${match[1]}`;

  const digits = s.replace(/[^\d]/g, "");
  if (!digits) return "";
  if (digits === "0") return "0";
  const dec = digits.startsWith("0") ? digits.slice(1).slice(0, 4) : digits.slice(0, 4);
  return `0,${dec}`;
}

export function parseCoeficiente(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const n = Number(String(value).trim().replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

export function formatearCoeficiente(value, { esDirecto = false } = {}) {
  if (value == null || value === "") return "";
  const asString = String(value).trim();
  if (/^0,\d{1,4}$/.test(asString) || asString === "1,0000" || asString === "1") {
    if (asString === "1") return esDirecto ? "1,0000" : asString;
    return asString;
  }
  const n = parseCoeficiente(value);
  if (n == null) return "";
  if (esDirecto && n === 1) return "1,0000";
  return n.toFixed(4).replace(".", ",");
}

export function getCoeficienteProvincia(reg) {
  if (!reg) return null;
  for (const v of [reg.coeficiente, reg.retencion]) {
    if (v != null && String(v).trim() !== "") return v;
  }
  return null;
}

export function tieneProvinciaCargada(reg) {
  if (!reg) return false;
  const id = reg.id_provincia;
  if (id != null && id !== "" && !Number.isNaN(Number(id))) return true;
  return getCoeficienteProvincia(reg) != null;
}

export function tieneVigenciaProvincia(reg, cicloFiscalActual) {
  if (!reg) return false;
  if (reg.vigencia_date != null && reg.vigencia_date !== "") return true;
  if (reg.vigencia != null && reg.vigencia !== "") return true;
  if (reg.fecha_vigencia_desde != null && reg.fecha_vigencia_desde !== "") return true;
  if (cicloFiscalActual?.fecha_vto) return true;
  return false;
}

/**
 * Formato válido:
 * - manual (string): 0,0000 a 0,9999 con exactamente 4 decimales
 * - padrón (numérico): 0 ≤ n < 1
 * - directo (convenio 3): también 1 / 1,0000
 */
export function esCoeficienteFormatoValido(value, { esDirecto = false, esPadron = false } = {}) {
  if (value == null || value === "") return false;

  if (esDirecto) {
    if (value === 1 || value === "1" || value === "1,0000") return true;
    const nDirecto = parseCoeficiente(value);
    if (nDirecto === 1) return true;
  }

  const asString = String(value).trim();
  if (/^0,\d{4}$/.test(asString)) return true;
  if (esDirecto && asString === "1,0000") return true;

  if (esPadron || typeof value === "number" || !asString.includes(",")) {
    const n = parseCoeficiente(value);
    if (n == null) return false;
    if (esDirecto && n === 1) return true;
    return n >= 0 && n < 1;
  }

  return false;
}

const MSG_COEF_VIGENCIA =
  "Complete coeficiente y vigencia en todas las provincias cargadas";
const MSG_COEF_FORMATO =
  "El coeficiente debe ser 0,0000 a 0,9999 con exactamente 4 decimales (ej.: 0,1234)";

function formatearSumaCoef(n) {
  return (Math.round(n * 10000) / 10000).toFixed(4).replace(".", ",");
}

/** Arma la lista única de provincias (manual pisa padrón si es la misma). */
export function armarProvinciasParaCoeficientes(regIIBBmanual = [], regIIBBpadron = []) {
  const byProv = new Map();
  let autoKey = 0;

  const push = (reg, esPadron) => {
    if (!tieneProvinciaCargada(reg)) return;
    const id = reg.id_provincia;
    const key =
      id != null && id !== "" && !Number.isNaN(Number(id))
        ? `id-${Number(id)}`
        : `row-${esPadron ? "p" : "m"}-${autoKey++}`;
    // Manual pisa padrón en la misma provincia
    if (!esPadron || !byProv.has(key)) {
      byProv.set(key, { reg, esPadron });
    }
  };

  (regIIBBpadron || []).forEach((reg) => push(reg, true));
  (regIIBBmanual || []).forEach((reg) => push(reg, false));

  return [...byProv.values()];
}

export function calcularSumaCoeficientes(
  regIIBBmanual = [],
  regIIBBpadron = [],
  { id_convenio } = {}
) {
  const esDirecto = esConvenioDirecto(id_convenio);
  const provincias = armarProvinciasParaCoeficientes(regIIBBmanual, regIIBBpadron);
  const suma = provincias.reduce((acum, { reg, esPadron }) => {
    const coef = getCoeficienteProvincia(reg);
    if (!esCoeficienteFormatoValido(coef, { esDirecto, esPadron })) return acum;
    return acum + (parseCoeficiente(coef) ?? 0);
  }, 0);
  return Math.round(suma * 10000) / 10000;
}

export function validar(
  regIIBBmanual,
  exencionesDePadron,
  exencionesFueraPadron,
  options = {}
) {
  const {
    regIIBBpadron = [],
    id_convenio,
    cicloFiscalActual,
  } = options;

  const exenciones = [...exencionesDePadron, ...exencionesFueraPadron];
  const errores = [];
  const esDirecto = esConvenioDirecto(id_convenio);

  const provinciasCargadas = armarProvinciasParaCoeficientes(
    regIIBBmanual,
    regIIBBpadron
  );

  if (provinciasCargadas.length > 0) {
    const incompletas = provinciasCargadas.filter(({ reg }) => {
      const coef = getCoeficienteProvincia(reg);
      const tieneCoef = coef != null && String(coef).trim() !== "";
      const tieneVig = tieneVigenciaProvincia(reg, cicloFiscalActual);
      return !tieneCoef || !tieneVig;
    });
    if (incompletas.length > 0) {
      errores.push(MSG_COEF_VIGENCIA);
    }

    const formatosInvalidos = provinciasCargadas.filter(({ reg, esPadron }) => {
      const coef = getCoeficienteProvincia(reg);
      if (coef == null || String(coef).trim() === "") return false;
      return !esCoeficienteFormatoValido(coef, { esDirecto, esPadron });
    });
    if (formatosInvalidos.length > 0) {
      errores.push(MSG_COEF_FORMATO);
    }

    const sumaRedondeada = calcularSumaCoeficientes(regIIBBmanual, regIIBBpadron, {
      id_convenio,
    });
    const hayInvalidos = formatosInvalidos.length > 0 || incompletas.length > 0;
    if (hayInvalidos || sumaRedondeada !== 1) {
      errores.push(
        `La suma de los coeficientes debe ser igual a 1 (actual: ${formatearSumaCoef(sumaRedondeada)}). Cada coeficiente va de 0,0000 a 0,9999.`
      );
    }
  }

  const erroresPorcentajesExcencion = exenciones.filter(
    (excencion) =>
      Number(excencion.porcentaje_exencion) > 100 ||
      Number(excencion.porcentaje_excencion) > 100 ||
      excencion.porcentaje_excencion === "0" ||
      excencion.porcentaje_excencion === "00" ||
      excencion.porcentaje_excencion === "000" ||
      excencion.porcentaje_exencion === "0" ||
      excencion.porcentaje_exencion === "00" ||
      excencion.porcentaje_exencion === "000"
  );
  if (erroresPorcentajesExcencion.length > 0) {
    errores.push("Ningun porcentaje debe ser 0 o mayor a 100");
  }

  const faltaVtoExcencion = exenciones.filter(
    (excencion) =>
      (!!excencion.porcentaje_excencion || !!excencion.porcentaje_exencion) &&
      !excencion.vto_excencion &&
      !excencion.vto_exencion
  );
  if (faltaVtoExcencion.length > 0) {
    errores.push("Completar Fecha Vencimiento Excención");
  }

  const faltaPorcExcencion = exenciones.filter(
    (excencion) =>
      (!!excencion.vto_excencion || !!excencion.vto_exencion) &&
      !excencion.porcentaje_excencion &&
      !excencion.porcentaje_exencion
  );
  if (faltaPorcExcencion.length > 0) {
    errores.push("Completar Porcentaje Excención");
  }

  return errores;
};
